import React from "react";
import { useTranslation } from "react-i18next";

const STATUS_STYLES = {
  Solid:         "bg-emerald-50 text-emerald-700 border-emerald-200",
  Moderate:      "bg-amber-50 text-amber-700 border-amber-200",
  Weak:          "bg-rose-50 text-rose-700 border-rose-200",
  "Not Started": "bg-slate-100 text-slate-600 border-slate-200",
  Completed:     "bg-emerald-50 text-emerald-700 border-emerald-200",
  "In Progress": "bg-sky-50 text-sky-700 border-sky-200",
  "Coming Soon": "bg-violet-50 text-violet-700 border-violet-200",
  Published:     "bg-emerald-50 text-emerald-700 border-emerald-200",
  Active:        "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const DOT_STYLES = {
  Solid: "bg-emerald-500",
  Moderate: "bg-amber-500",
  Weak: "bg-rose-500",
  "Not Started": "bg-slate-300",
  Active: "bg-emerald-500",
};

// Translate the English status key; fall back to the key itself if no translation found.
const useStatusLabel = () => {
  const { t } = useTranslation();
  return (value) => t(`status.${value}`, { defaultValue: value });
};

export const StatusBadge = ({ value, compact = false, withDot = false, className = "" }) => {
  const tr = useStatusLabel();
  const style = STATUS_STYLES[value] || "bg-slate-100 text-slate-700 border-slate-200";
  const label = tr(value);
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${style} ${className}`}
      data-testid={`status-badge-${String(value).toLowerCase().replace(/\s+/g, '-')}`}
    >
      {withDot && <span className={`h-1.5 w-1.5 rounded-full ${DOT_STYLES[value] || 'bg-slate-300'}`} />}
      {compact ? label.slice(0, 1) : label}
    </span>
  );
};

export const LegendDots = ({ items = ["Solid", "Moderate", "Weak", "Not Started"] }) => {
  const tr = useStatusLabel();
  return (
    <div className="flex flex-wrap items-center gap-4 text-xs text-slate-600">
      {items.map((v) => (
        <span key={v} className="flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${DOT_STYLES[v]}`} />
          {tr(v)}
        </span>
      ))}
    </div>
  );
};

export const QuestionTypeBadge = ({ type }) => {
  const { t } = useTranslation();
  const styles = {
    Conceptual: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Applied: "bg-orange-50 text-orange-700 border-orange-200",
    Theoretical: "bg-violet-50 text-violet-700 border-violet-200",
  };
  return (
    <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${styles[type] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
      {t(`types.${type}`, { defaultValue: type })}
    </span>
  );
};
